import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import sanitizeHtml from 'sanitize-html';

import { appendUtmToUrl, getSessionUtmParams, isKHealthUrl } from '@/libs/utm';
import { trackInteraction } from '@/utils/analytics';
import { composeRefs } from '@/utils/composeRefs';
import sanitizeWhiteList from '@/utils/sanitize-html.whitelist';

import styles from './RichTextEditor.module.scss';

/**
 * Sanitizes the authored HTML. When UTM params are supplied, K Health anchors get them
 * appended to their href; every other anchor is left untouched.
 */
const sanitizeText = (text, utmParams) => {
    if (!utmParams) {
        return sanitizeHtml(text, sanitizeWhiteList);
    }
    return sanitizeHtml(text, {
        ...sanitizeWhiteList,
        transformTags: {
            ...sanitizeWhiteList.transformTags,
            a: (tagName, attribs) => ({
                tagName,
                attribs: isKHealthUrl(attribs.href)
                    ? { ...attribs, href: appendUtmToUrl(attribs.href, utmParams) }
                    : attribs,
            }),
        },
    });
};

/**
 * @see https://github.com/adobe/aem-project-archetype/blob/develop/src/main/archetype/ui.frontend.react/src/components/Text/Text.js
 */
export const RichTextEditor = React.forwardRef((props, ref) => {
    const {
        className,
        text,
        analytics,
        onAnchorClick,
        isTypeH3 = false,
        withUtmParam = false,
    } = props;
    const internalRef = React.useRef();
    // Read after mount so SSR and the first client render get bare hrefs (no hydration mismatch)
    const [utmParams, setUtmParams] = React.useState(null);

    React.useEffect(() => {
        setUtmParams(withUtmParam ? getSessionUtmParams() : null);
    }, [withUtmParam]);

    const html = sanitizeText(text, utmParams);

    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const anchorsElement = internalRef.current.querySelectorAll('a');

        const handleAnchorClick = (e) => {
            if (typeof onAnchorClick === 'function') {
                onAnchorClick(e);
            }
            trackInteraction({
                interactionType: 'inline_link',
                actionLabel: e.target.textContent,
                linkHref: e.target.getAttribute('href'),
                selector: 'a',
                ...analytics,
            });
            console.log(
                'TODO - when we have the navigate strategy we can handle it in a appropriated way',
            );
        };

        anchorsElement.forEach((a) => {
            a.setAttribute('data-trigger', analytics?.selector || 'a');
            a.addEventListener('click', handleAnchorClick);
        });

        return () => {
            anchorsElement.forEach((a) => {
                a.removeEventListener('click', handleAnchorClick);
            });
        };
        // `html` re-binds listeners when the markup is replaced (e.g. after UTM augmentation)
    }, [analytics, onAnchorClick, html]);

    return (
        <div
            ref={composeRefs(ref, internalRef)}
            className={classnames(
                styles['rich-text-editor'],
                className,
                styles[`${isTypeH3 ? 'rich-text-editor__h3' : ''}`],
            )}
            data-rte-editelement
            dangerouslySetInnerHTML={{
                __html: html,
            }}
        />
    );
});

RichTextEditor.displayName = 'RichTextEditor';

RichTextEditor.propTypes = {
    /**
     * HTML content
     */
    text: PropTypes.string,
    /**
     * switch the style from H2 to H3 keeping H2 as TAG
     */
    isTypeH3: PropTypes.bool,
    /**
     * Append the session's UTM params to links pointing at a K Health destination
     */
    withUtmParam: PropTypes.bool,
};
